import { useState } from "react";
import { useMe } from "@/api/me";
import { AskUnboxDrawer } from "@/pages/ask/components/ask-unbox-drawer";
import type { ReportRequest } from "@/pages/ask/components/ReportPicker";
import { brandFromMerchant } from "@/pages/ask/lib/data";
import { useMerchant } from "@/providers/MerchantProvider";
import { AskUnboxHero } from "./components/ask-unbox-hero";
import { DistributionSection } from "./components/distribution-section";
import { SetupChecklist } from "./components/setup-checklist";
import type { AskRequest } from "./lib/ask-prompts";
import { useHomeRange } from "./lib/use-home-range";

export function HomePage() {
  const { data: user } = useMe();
  const merchantId = useMerchant();
  const { range, maxSelectableDate, setSelection, ready } = useHomeRange();
  const [drawer, setDrawer] = useState<{
    open: boolean;
    sessionKey: number;
    question?: string;
    report?: ReportRequest;
    suggestions?: string[];
    title?: string;
  }>({ open: false, sessionKey: 0 });

  const openDrawer = (chat: AskRequest & { report?: ReportRequest }) =>
    setDrawer((current) => ({ open: true, sessionKey: current.sessionKey + 1, ...chat }));

  const name = user ? [user.firstName, user.lastName].filter(Boolean).join(" ").trim() : undefined;

  const merchant =
    user?.merchants.find((m) => m.id === merchantId) ??
    user?.workspaces?.flatMap((w) => w.merchants).find((m) => m.id === merchantId);
  const scope = brandFromMerchant({ id: merchantId, name: merchant?.name ?? "", vertical: merchant?.vertical });

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto">
      <AskUnboxHero
        userName={name || undefined}
        userEmail={user?.email}
        onAsk={(question) => openDrawer({ question })}
        onGenerateReport={(report) => openDrawer({ report })}
        brandName={scope.displayName || scope.name}
        range={range}
        rangeReady={ready}
      />

      <DistributionSection
        onAsk={openDrawer}
        range={range}
        rangeReady={ready}
        maxSelectableDate={maxSelectableDate}
        onSelectionChange={setSelection}
      />

      <AskUnboxDrawer
        isOpen={drawer.open}
        sessionKey={drawer.sessionKey}
        onClose={() => setDrawer((current) => ({ ...current, open: false }))}
        initialQuestion={drawer.question}
        initialReport={drawer.report}
        suggestions={drawer.suggestions}
        emptyStateTitle={drawer.title}
        userEmail={user?.email ?? ""}
        userName={name || undefined}
        scope={scope}
      />

      <SetupChecklist currentUserId={user?.id} />
    </div>
  );
}

export default HomePage;
